import { Box, Table, Text } from "@mantine/core"
import { useTranslation } from "react-i18next"
import grid from "../ui/data-grid.module.css"
import detail from "../../pages/sales-invoice/details.module.css"
import { currencyFormat } from "../../utils"
import classes from "./BuyerInvoiceDetails.module.css"

const money = (value: number) => currencyFormat(value, { withSymbol: false })

// On both invoice paths `totalVatAmount`, `taxAmount` and `lineTotalAmount` are
// stored per line with the quantity already folded in, so they are shown as-is.
// Whether `lineTotalAmount` contains the VAT depends on the path that wrote the
// invoice; the footer rows (computed by the page) reconcile that, not the rows.
function lineTotal(line: any): number {
  if (line?.lineTotalAmount != null) return Number(line.lineTotalAmount)
  return Number(line?.quantity ?? 0) * Number(line?.price ?? 0)
}

// Same rule as effectiveUnitPrice in de-invoice-service logics/pdf/invoice.ts:
// order-level discounts are folded into lineTotalAmount without touching the
// line's price, so the price actually charged per unit is total / quantity.
// Using it keeps qty x price = total on the row and matches the PDF the buyer
// downloads from the same page.
function unitPrice(line: any): number {
  const total = line?.lineTotalAmount == null ? NaN : Number(line.lineTotalAmount)
  const qty = line?.quantity == null ? NaN : Number(line.quantity)
  if (!Number.isFinite(total) || !Number.isFinite(qty) || qty <= 0) return Number(line?.price) || 0
  return Math.round((total / qty) * 100) / 100
}

// A discount is only applied by the backend when both type and value are set.
function hasDiscount(line: any): boolean {
  return Boolean(line?.discountType) && Number(line?.discountValue ?? 0) > 0
}

function discountLabel(line: any): string {
  if (!hasDiscount(line)) return "-"
  const value = Number(line.discountValue)
  return line.discountType === "PERCENTAGE" ? `${value}%` : `−${money(value)}`
}

export type LineTotals = {
  taxAmount: number
  additionalAmount: number
  shippingAmount: number
  vatAmount: number
  vatIncluded: boolean
  discount: number
  discountPct: number
  grandTotal: number
}

type Props = {
  lines: any[]
  additionalLines: any[]
  totals: LineTotals
}

export function InvoiceLineItems({ lines, additionalLines, totals }: Props) {
  const { t } = useTranslation(["invoice", "common"])

  if (lines.length === 0 && additionalLines.length === 0) {
    return <Box className={detail.emptyInline}>{t("common:noData")}</Box>
  }

  const showTax = lines.some((line) => line?.hasTax || Number(line?.taxAmount ?? 0) > 0)
  const showDiscount = lines.some(hasDiscount)
  // Бараа, Тоо, Нэгж үнэ, НӨАТ, [НХАТ], [Хөнгөлөлт], Дүн
  const colCount = 5 + Number(showTax) + Number(showDiscount)
  const labelSpan = colCount - 1

  const footRow = (key: string, label: string, value: string, extraClass = "") => (
    <Table.Tr key={key} className={`${detail.tfootRow} ${extraClass}`}>
      <Table.Td colSpan={labelSpan} className={detail.num}>
        {label}
      </Table.Td>
      <Table.Td className={`${detail.num} ${detail.amountNowrap}`}>{value}</Table.Td>
    </Table.Tr>
  )

  return (
    <Box className={`${grid.wrap} ${detail.lineTable}`}>
      <Table className={grid.table} horizontalSpacing={0} verticalSpacing={0} withRowBorders={false}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{t("invoice:detail.lineCol.product")}</Table.Th>
            <Table.Th className={detail.num}>{t("invoice:detail.lineCol.qty")}</Table.Th>
            <Table.Th className={detail.num}>{t("invoice:detail.lineCol.unitPrice")}</Table.Th>
            <Table.Th className={detail.num}>{t("invoice:detail.totals.vat")}</Table.Th>
            {showTax && <Table.Th className={detail.num}>{t("invoice:detail.totals.tax")}</Table.Th>}
            {showDiscount && <Table.Th className={detail.num}>{t("invoice:detail.lineCol.discount")}</Table.Th>}
            <Table.Th className={detail.num}>{t("invoice:detail.lineCol.amount")}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {lines.map((line, index) => {
            const sub = [line?.skuCode, line?.brand, line?.optionValue].filter(Boolean).join(" · ")
            return (
              <Table.Tr key={line?.id ?? index}>
                <Table.Td>
                  <Text className={`${detail.lineName} ${classes.nameCell}`} lineClamp={2} title={line?.name || undefined}>
                    {line?.name || "-"}
                  </Text>
                  {(sub || line?.unit) && (
                    <Box className={detail.lineSku}>
                      {sub && (
                        <Text component="span" className={detail.lineSkuCode}>
                          {sub}
                        </Text>
                      )}
                      {sub && line?.unit && (
                        <Text component="span" className={detail.lineSkuUnit}>
                          {" · "}
                        </Text>
                      )}
                      {line?.unit && (
                        <Text component="span" className={detail.lineSkuUnit}>
                          {line.unit}
                        </Text>
                      )}
                    </Box>
                  )}
                </Table.Td>
                <Table.Td className={detail.num}>{Number(line?.quantity ?? 0)}</Table.Td>
                <Table.Td className={`${detail.num} ${detail.amountNowrap}`}>{money(unitPrice(line))}</Table.Td>
                <Table.Td className={`${detail.num} ${detail.amountNowrap}`}>
                  {money(Number(line?.totalVatAmount ?? 0))}
                </Table.Td>
                {showTax && (
                  <Table.Td className={`${detail.num} ${detail.amountNowrap}`}>
                    {money(Number(line?.taxAmount ?? 0))}
                  </Table.Td>
                )}
                {showDiscount && (
                  <Table.Td
                    className={`${detail.num} ${hasDiscount(line) ? detail.lineDiscountActive : detail.lineDiscount}`}
                  >
                    {discountLabel(line)}
                  </Table.Td>
                )}
                <Table.Td className={`${detail.num} ${detail.amountNowrap} ${detail.lineTotal}`}>
                  {money(lineTotal(line))}
                </Table.Td>
              </Table.Tr>
            )
          })}

          {additionalLines.length > 0 && (
            <Table.Tr className={classes.groupRow}>
              <Table.Td colSpan={colCount}>
                <Text component="span" className={classes.groupLabel}>
                  {t("invoice:productForm.additionalItems")}
                </Text>
                <Text component="span" className={detail.cardSub}>
                  {" · "}
                  {t("invoice:detail.lineRows", { count: additionalLines.length })}
                </Text>
              </Table.Td>
            </Table.Tr>
          )}
          {additionalLines.map((line, index) => (
            <Table.Tr key={`add-${line?.id ?? index}`}>
              <Table.Td>
                <Text className={`${detail.lineName} ${classes.nameCell}`} lineClamp={2} title={line?.name || undefined}>
                  {line?.name || "-"}
                </Text>
                {line?.unit && (
                  <Box className={detail.lineSku}>
                    <Text component="span" className={detail.lineSkuUnit}>
                      {line.unit}
                    </Text>
                  </Box>
                )}
              </Table.Td>
              <Table.Td className={detail.num}>{Number(line?.quantity ?? 0)}</Table.Td>
              <Table.Td className={`${detail.num} ${detail.amountNowrap}`}>{money(unitPrice(line))}</Table.Td>
              <Table.Td className={`${detail.num} ${detail.lineDiscount}`}>-</Table.Td>
              {showTax && <Table.Td className={`${detail.num} ${detail.lineDiscount}`}>-</Table.Td>}
              {showDiscount && <Table.Td className={`${detail.num} ${detail.lineDiscount}`}>-</Table.Td>}
              <Table.Td className={`${detail.num} ${detail.amountNowrap} ${detail.lineTotal}`}>
                {money(lineTotal(line))}
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
        <Table.Tfoot>
          {totals.taxAmount > 0 && footRow("tax", t("invoice:detail.totals.tax"), money(totals.taxAmount))}
          {totals.additionalAmount > 0 &&
            footRow("additional", t("invoice:detail.totals.additionalLines"), money(totals.additionalAmount))}
          {totals.shippingAmount > 0 &&
            footRow("shipping", t("invoice:detail.totals.shipping"), money(totals.shippingAmount))}
          {!totals.vatIncluded && totals.vatAmount > 0 &&
            footRow("vat", t("invoice:detail.totals.vat"), money(totals.vatAmount))}
          {totals.discount > 0 &&
            footRow(
              "discount",
              `${t("invoice:detail.totals.discount")}${totals.discountPct > 0 ? ` (${totals.discountPct}%)` : ""}`,
              `−${money(totals.discount)}`,
              detail.tfootDiscount,
            )}
          {totals.vatIncluded && totals.vatAmount > 0 &&
            footRow("vatOf", t("invoice:detail.totals.vatOfWhich"), money(totals.vatAmount))}
          <Table.Tr className={detail.tfootGrand}>
            <Table.Td colSpan={labelSpan} className={detail.num}>
              {t("invoice:detail.totals.grandShort")}
            </Table.Td>
            <Table.Td className={`${detail.num} ${detail.tfootGrandVal}`}>
              {currencyFormat(totals.grandTotal)}
            </Table.Td>
          </Table.Tr>
        </Table.Tfoot>
      </Table>
    </Box>
  )
}
